import AnalyticsWebClient, {
  envType,
  originType,
  type ScreenEventPayload,
  tenantType,
  userType,
} from '@atlassiansox/analytics-web-client';

import { getConfig } from '../config/envConfig';
import { EnvType, getEnv } from '../config/env';
import type { ServiceType } from '#definitions/context';

function getServerName() {
  return window.location.hostname;
}

const { LOCAL, DEV, STAGING, PROD } = envType;
const ENV_MAP: Record<EnvType, envType> = {
  local: LOCAL,
  ddev: DEV,
  stg: STAGING,
  'stg-fedm': STAGING,
  prod: PROD,
  'prod-fedm': PROD,
};

const analyticsConfig = getConfig().analytics;

// https://developer.atlassian.com/platform/analytics-pipeline/getting-started/event-types/#event-types
type EventType =
  // Track events fire when an actor successfully completes a product action.
  // Track events indicate an action has occurred, not the intention to take an action.
  | 'track'
  // Analytics events that fire when a user navigates to a particular screen, tab, drawer, modal, or inline-dialog
  // Tabs, drawers, modals, and inline-dialogs are included in the definition as they often take over the screen and require user input.
  | 'screen'
  // Operational events are used to help measure usages or performance of implementation detail NOT user actions.
  | 'operation'
  // UI events fire when a user interacts with a user interface element such as a button, text field, or link.
  // They enable us to see how users are performing certain tasks.
  | 'ui';
export type CallBackFn = () => void;

interface State {
  analyticsScheme: string;
  analyticsServer: string;
  server: string;
  product: string;
  isReadyToSend: boolean;
  pendingEventsQueue: {
    eventType: EventType;
    // TODO: add event types
    eventData: any;
    eventCallback?: CallBackFn;
  }[];
  storage_key: string;
  clientId?: string;
  customer?: string;
  profile?: string;
  serviceType?: ServiceType;
}

export const DEFAULT_PRODUCT_NAME = 'identity';
const SCREEN_NAME = 'oauthConsentScreen';

export type FailureReasonType =
  | 'remote4xxError'
  | 'remote5xxError'
  | 'other'
  // Custom failure reason - not part of the spec
  // remember to update the event registry when updating this list
  | 'jwtValidationError'
  | 'infoInputValidationError';

class AnalyticsClient {
  isInitialLoad: boolean;
  state: State;
  analyticsWebClient: AnalyticsWebClient;

  constructor(options: Partial<State> = {}) {
    this.state = {
      analyticsScheme: analyticsConfig.scheme,
      analyticsServer: analyticsConfig.server,
      server: getServerName(),
      product: DEFAULT_PRODUCT_NAME,
      isReadyToSend: false,
      pendingEventsQueue: [],
      storage_key: 'oauth.consent.analytics',
      ...options,
    };

    // New GAS pipeline client. Follow this docs https://bitbucket.org/atlassian/analytics-web-client/src/master/ for more detail
    this.analyticsWebClient = new AnalyticsWebClient({
      env: ENV_MAP[getEnv()],
      product: DEFAULT_PRODUCT_NAME,
      origin: originType.WEB,
    });

    // sending apdex events needs to know whether this is the initial load of the app or a transition.
    // Following variable helps us identify that.
    this.isInitialLoad = true;
  }

  getAnalyticsWebClient() {
    return this.analyticsWebClient;
  }

  sendTrackEvent(eventData: any) {
    void this.analyticsWebClient.sendTrackEvent(eventData);
  }

  setUserId(userId: string) {
    this.analyticsWebClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
  }

  setCloudId(cloudId: string | null) {
    if (!cloudId) {
      return this.analyticsWebClient.setTenantInfo(tenantType.NONE);
    }
    this.analyticsWebClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
  }

  updateState(state: Partial<State>) {
    this.state = {
      ...this.state,
      ...state,
    };
  }

  /**
   * Checks whether the library has been initialized and decides whether the event should be sent or cached
   * @param {*} eventType Values could be 'track', 'screen', 'operation, 'ui'
   * @param {*} eventData Data of the event
   * @param {Function} cb Callback is called when the event is sent successfully.
   */
  private canEventBeSent(
    eventType: EventType,
    eventData: any,
    cb?: CallBackFn,
  ): boolean {
    if (this.state.isReadyToSend) {
      return true;
    }

    // add to `pendingEventsQueue` in order to send the event later
    this.state.pendingEventsQueue.push({
      eventType,
      eventData,
      // `eventCallback` is called when `pendingEventsQueue` is processed and eventData is sent
      eventCallback: cb,
    });

    return false;
  }

  pushTrackEvent(eventData: any, cb?: CallBackFn): void {
    if (this.canEventBeSent('track', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      void this.analyticsWebClient.sendTrackEvent(event, cb);
    }
  }

  pushUIEvent(eventData: any, cb?: CallBackFn): void {
    if (this.canEventBeSent('ui', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      void this.analyticsWebClient.sendUIEvent(event, cb);
    }
  }

  pushOperationalEvent(eventData: any, cb?: CallBackFn): void {
    if (this.canEventBeSent('operation', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      void this.analyticsWebClient.sendOperationalEvent(event, cb);
    }
  }

  pushScreenEvent(eventData: ScreenEventPayload, cb?: CallBackFn): void {
    if (this.canEventBeSent('screen', eventData, cb)) {
      void this.analyticsWebClient.sendScreenEvent(eventData, cb);
    }
  }

  generateAttributes() {
    return {
      customer: this.state.customer,
      profile: this.state.profile,
      serviceType: this.state.serviceType,
    };
  }

  async sendConsentScreenEvent() {
    return this.analyticsWebClient.sendScreenEvent({
      name: SCREEN_NAME,
      attributes: this.generateAttributes(),
    });
  }

  // https://hello.atlassian.net/wiki/spaces/AAPT/pages/581609987/RFC+Analytics+-+Frontend+Errors+-+Package+Component+Failed#Specification
  async sendErrorEvent(reason: FailureReasonType) {
    return this.analyticsWebClient.sendOperationalEvent({
      action: 'failed',
      actionSubject: 'oauthConsent',
      source: SCREEN_NAME,
      attributes: {
        ...this.generateAttributes(),
        failureReason: reason,
      },
    });
  }

  async sendButtonClickEvent(buttonName: string) {
    return this.analyticsWebClient.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: buttonName,
      attributes: this.generateAttributes(),
      source: SCREEN_NAME,
    });
  }

  buildEventPayload(eventData: any): any {
    return {
      containerType: null,
      containerId: null,
      objectType: null,
      objectId: null,
      source: null,

      actionSubject: null,
      action: null,
      actionSubjectId: null,
      attributes: null,
      tags: [],
      ...eventData,
    };
  }

  /**
   *  Start up E-MAU events in the new analytics pipeline (GAS v3)
   *  See: https://extranet.atlassian.com/display/BPO/DACI%3A+E-MAU+Definition
   */
  async startUIViewedEvent() {
    this.analyticsWebClient.startUIViewedEvent();
    this.state.isReadyToSend = true;
    this.flushPendingEventsQueue();
  }

  /**
   * Flush all the pending events in the queue
   */
  flushPendingEventsQueue() {
    this.state.pendingEventsQueue.forEach((element: any) => {
      const { eventType, eventData, eventCallback } = element;

      switch (eventType) {
        case 'track':
          this.pushTrackEvent(eventData, eventCallback);
          break;
        case 'ui':
          this.pushUIEvent(eventData, eventCallback);
          break;
        case 'screen':
          this.pushScreenEvent(eventData, eventCallback);
          break;
        case 'operation':
          this.pushOperationalEvent(eventData, eventCallback);
          break;
        default:
          console.error('Invalid Event :', element);
          break;
      }
    });
  }
}

export default AnalyticsClient;

/**
 * In order for the app to have the same analytics client instances we'll preserve them in a local const
 */
let preservedAnalyticsClients: AnalyticsClient;

export const getAnalyticsClient = (store?: any) => {
  if (!preservedAnalyticsClients || !store) {
    preservedAnalyticsClients = new AnalyticsClient(store);
  }

  return preservedAnalyticsClients;
};
